import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  getLocation,
  getAlerts,
  getAllAlerts,
  setDeviceId,
  updateInstallationStatus,
  markAlertAsViewed,
  generateSoftenerCsv,
  generateWaterFilterCsv,
  generateReverseOsmosisCsv,
  getLocations,
  getPendingCount,
  getLastHealthCheckForLocation,
  startHealthCheck,
  showAlert,
} from 'actions/index';
import Loader from 'components/Loader';
import PropTypes from 'prop-types';
import LocationDetailView from './LocationDetailView';

class LocationDetailContainer extends Component {
  state = {
    relatedLocations: [],
    hasRequestedRelatedLocations: false,
  };

  componentDidMount() {
    const {
      fetchLocation,
      fetchAlerts,
      fetchLastHealthCheckForLocation,
      getPendingCount,
      match: { params },
    } = this.props;
    const { id } = params;

    fetchLocation(id);
    fetchAlerts({ location_id: id });
    if (id) fetchLastHealthCheckForLocation(id);
    getPendingCount();
  }

  componentDidUpdate(prevProps) {
    const { location, locationsList, locationsListLoading } = this.props;

    const { hasRequestedRelatedLocations } = this.state;

    // When location data is loaded, fetch related locations by account and address
    if (!prevProps.location && location && !hasRequestedRelatedLocations) {
      this.requestRelatedLocations(location);
    }

    // When locations list is loaded, update relatedLocations in state
    if (
      prevProps.locationsListLoading &&
      !locationsListLoading &&
      locationsList &&
      locationsList.rows &&
      location
    ) {
      this.updateRelatedLocations(locationsList.rows, location.id);
    }
  }

  requestRelatedLocations = location => {
    const { fetchLocations } = this.props;

    if (location && location.Account && location.address_line) {
      // Extract street number and name only from address
      // const addressParts = location.address_line.split(' ');
      // const streetAddress =
      //   addressParts.length >= 2 ? `${addressParts[0]} ${addressParts[1]}` : location.address_line;

      // Find locations with same account and address (partial)
      fetchLocations({
        account_id: location.Account.id,
        // Not sending address parameter to API since we'll filter locally
        // address: streetAddress
      });

      this.setState({ hasRequestedRelatedLocations: true });
    }
  };

  updateRelatedLocations = (locationRows, currentLocationId) => {
    const { location } = this.props;

    if (!location || !location.address_line) {
      return;
    }

    // Extract street number and name only from current location's address
    const addressParts = location.address_line.split(' ');
    const streetAddress =
      addressParts.length >= 2 ? `${addressParts[0]} ${addressParts[1]}` : location.address_line;

    // Filter for locations with the same account and address, excluding current location
    const relatedLocations = locationRows.filter(loc => {
      // Skip current location
      if (loc.id === currentLocationId) {
        return false;
      }

      // Make sure it's in the same account
      if (loc.Account && location.Account && loc.Account.id !== location.Account.id) {
        return false;
      }

      // Check if this location's address starts with the same street number and name
      if (loc.address_line) {
        const locAddressParts = loc.address_line.split(' ');
        const locStreetAddress =
          locAddressParts.length >= 2
            ? `${locAddressParts[0]} ${locAddressParts[1]}`
            : loc.address_line;

        const isMatch = locStreetAddress.toLowerCase() === streetAddress.toLowerCase();

        return isMatch;
      }

      return false;
    });

    this.setState({ relatedLocations });
  };

  render() {
    const {
      location,
      loading,
      alerts,
      alertsPages,
      alertsLoading,
      alertsCurrentPage,
      fetchAlerts,
      fetchAllAlerts,
      allAlerts,
      setDeviceId,
      updateInstallationStatus,
      markAlertAsViewed,
      role_id,
      role_code,
      userEmail,
      generateSoftenerCsv,
      generateWaterFilterCsv,
      generateReverseOsmosisCsv,
      lastHealthCheckForLocation,
      lastHealthCheckForLocationLoading,
      startHealthCheck,
      startHealthCheckLoading,
      showAlert,
    } = this.props;
    const { relatedLocations } = this.state;

    if (loading) {
      return <Loader size={200} block />;
    }

    if (!location) {
      return null;
    }

    return (
      <LocationDetailView
        loading={loading}
        location={location}
        alerts={alerts}
        alertsPages={alertsPages}
        alertsLoading={alertsLoading}
        alertsCurrentPage={alertsCurrentPage}
        fetchAlerts={fetchAlerts}
        fetchAllAlerts={fetchAllAlerts}
        allAlerts={allAlerts}
        setDeviceId={setDeviceId}
        updateInstallationStatus={updateInstallationStatus}
        markAlertAsViewed={markAlertAsViewed}
        role_id={role_id}
        role_code={role_code}
        userEmail={userEmail}
        generateSoftenerCsv={generateSoftenerCsv}
        generateWaterFilterCsv={generateWaterFilterCsv}
        generateReverseOsmosisCsv={generateReverseOsmosisCsv}
        relatedLocations={relatedLocations}
        showAlert={showAlert}
        lastHealthCheckForLocation={lastHealthCheckForLocation}
        lastHealthCheckForLocationLoading={lastHealthCheckForLocationLoading}
        startHealthCheck={startHealthCheck}
        startHealthCheckLoading={startHealthCheckLoading}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    location: state.locations.item,
    loading: state.locations.loading,
    allAlerts: state.alerts.allAlerts,
    alerts: state.alerts.list,
    alertsLoading: state.alerts.loading,
    alertsPages: state.alerts.pages,
    alertsCurrentPage: state.alerts.currentPage,
    role_id: state.auth.profile.role_id,
    role_code: state.auth.profile.role_code,
    userEmail: state.auth.profile?.email,
    locationsList: state.locations.list
      ? {
          rows: state.locations.list,
          pages: state.locations.pages,
          currentPage: state.locations.currentPage,
        }
      : null,
    locationsListLoading: state.locations.loading,
    lastHealthCheckForLocation: state.healthCheck.lastHealthCheckForLocation,
    lastHealthCheckForLocationLoading: state.healthCheck.lastHealthCheckForLocationLoading,
    startHealthCheckLoading: state.healthCheck.startHealthCheckLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchAlerts: params => dispatch(getAlerts(params)),
    fetchAllAlerts: params => dispatch(getAllAlerts(params)),
    fetchLocation: id => dispatch(getLocation(id)),
    fetchLocations: params => dispatch(getLocations(params)),
    fetchLastHealthCheckForLocation: locationId =>
      dispatch(getLastHealthCheckForLocation(locationId)),
    setDeviceId: params => dispatch(setDeviceId(params)),
    updateInstallationStatus: (id, data) => dispatch(updateInstallationStatus(id, data)),
    markAlertAsViewed: params => dispatch(markAlertAsViewed(params)),
    getPendingCount: () => dispatch(getPendingCount()),
    generateSoftenerCsv: params => dispatch(generateSoftenerCsv(params)),
    generateWaterFilterCsv: params => dispatch(generateWaterFilterCsv(params)),
    generateReverseOsmosisCsv: params => dispatch(generateReverseOsmosisCsv(params)),
    startHealthCheck: params => dispatch(startHealthCheck(params)),
    showAlert: (message, options) => dispatch(showAlert(message, options)),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(LocationDetailContainer);

LocationDetailContainer.propTypes = {
  alerts: PropTypes.array,
  alertsCurrentPage: PropTypes.number,
  alertsLoading: PropTypes.bool,
  alertsPages: PropTypes.number,
  allAlerts: PropTypes.array,
  fetchAlerts: PropTypes.func,
  fetchAllAlerts: PropTypes.func,
  fetchLastHealthCheckForLocation: PropTypes.func,
  fetchLocation: PropTypes.func,
  fetchLocations: PropTypes.func,
  generateReverseOsmosisCsv: PropTypes.func,
  generateSoftenerCsv: PropTypes.func,
  generateWaterFilterCsv: PropTypes.func,
  getPendingCount: PropTypes.func,
  lastHealthCheckForLocation: PropTypes.object,
  lastHealthCheckForLocationLoading: PropTypes.bool,
  loading: PropTypes.bool,
  location: PropTypes.object,
  locationsList: PropTypes.object,
  locationsListLoading: PropTypes.bool,
  markAlertAsViewed: PropTypes.func,
  match: PropTypes.object,
  role_code: PropTypes.string,
  role_id: PropTypes.number,
  setDeviceId: PropTypes.func,
  showAlert: PropTypes.func,
  startHealthCheck: PropTypes.func,
  startHealthCheckLoading: PropTypes.bool,
  updateInstallationStatus: PropTypes.func,
  userEmail: PropTypes.string,
};
